"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateXlsx2CsvExtractConfigFromXLSX = exports.generateTapeDefinitionFromCSV = exports.generateTypeDefinition = exports.generateTapeFieldStats = void 0;
/* eslint-disable no-plusplus */
const edit_1 = __importDefault(require("@sheet/edit"));
const lodash = __importStar(require("lodash"));
const luxon_1 = require("luxon");
const moment_guess_1 = __importDefault(require("moment-guess"));
const common_1 = require("@finley-technologies/common");
const { capitalize } = lodash;
// returns number of digits found after decimal point
const numDecimal = (x) => {
    const [, fractionPart] = String(x).split('.');
    return fractionPart?.length ?? 0;
};
const isDate = (values) => {
    // pass 1, find potential date formats.
    const dateFormats = new Set();
    const nonDateFound = values.find((value) => {
        try {
            const dateFormatsArrayOrString = (0, moment_guess_1.default)(value);
            const dateFormatsArray = Array.isArray(dateFormatsArrayOrString)
                ? dateFormatsArrayOrString
                : [dateFormatsArrayOrString];
            dateFormatsArray.forEach((format) => dateFormats.add(format));
            return false;
        }
        catch (ex) {
            // not a date.
            return true;
        }
    });
    if (nonDateFound || dateFormats.size === 0) {
        return null;
    }
    // try to reduce the date to a single luxon format.
    const luxonFormatsMap = new Map();
    const dateFormatsArray = Array.from(dateFormats.values());
    // create an array of luxon formats
    dateFormatsArray.forEach((momentFormat) => {
        // moment format to luxon.
        const luxonFormat = momentFormat
            .replace(/Y/g, 'y')
            .replace(/D/g, 'd')
            .replace(/M/g, 'L');
        luxonFormatsMap.set(luxonFormat, { match: 0, nomatch: 0 });
    });
    let bestMatchLuxonFormat = null;
    let maxMatches = 0;
    values.forEach((value) => {
        luxonFormatsMap.forEach(({ match, nomatch }, luxonFormat) => {
            try {
                if (luxon_1.DateTime.fromFormat(value, luxonFormat).isValid) {
                    const matches = match + 1;
                    luxonFormatsMap.set(luxonFormat, { match: matches, nomatch });
                    if (maxMatches < matches) {
                        bestMatchLuxonFormat = luxonFormat;
                        maxMatches = matches;
                    }
                }
                else {
                    luxonFormatsMap.set(luxonFormat, {
                        match,
                        nomatch: nomatch + 1,
                    });
                }
            }
            catch {
                // this date did not parse.
            }
        });
    });
    if (bestMatchLuxonFormat &&
        luxonFormatsMap.get(bestMatchLuxonFormat)?.nomatch === 0) {
        return bestMatchLuxonFormat;
    }
    return null;
};
const generateBasicStats = (values, defaultValue) => {
    let numbers = 0;
    let strings = 0;
    let nulls = 0;
    const unique = new Set(); // includes only non-null values.
    values.forEach((value) => {
        const isNull = value === defaultValue || value === null || value === undefined;
        if (isNull) {
            nulls++;
        }
        else if (!Number.isNaN(Number(value))) {
            numbers++;
        }
        else if (typeof value === 'string') {
            strings++;
        }
        else {
            (0, common_1.finleyAssert)(false, `Unexpected Value: ${value}, type: ${typeof value}`);
        }
        if (!isNull) {
            unique.add(value);
        }
    });
    return {
        numbers,
        strings,
        nulls,
        unique,
    };
};
const detectType = (basicStats) => {
    const { numbers, strings, unique, nulls } = basicStats;
    const isOptional = nulls > 0;
    (0, common_1.debugLog)({ numbers, strings, unique, nulls });
    const uniqueValues = Array.from(unique.values());
    if (strings === 0) {
        // these are all numbers
        let decimals = 0; // number of digits after decimal point.
        let max = Number.NEGATIVE_INFINITY;
        let min = Number.POSITIVE_INFINITY;
        uniqueValues.forEach((value) => {
            const numValue = Number(value);
            max = Math.max(numValue, max);
            min = Math.min(numValue, min);
            decimals = Math.max(decimals, numDecimal(numValue));
        });
        // capMarkets prefers float over integer when there are all 0s
        // https://linear.app/finleycms/issue/ENG-2497/generate-config-from-sample-csv-should-assume-floats
        const onlyZeros = min === 0 && max === 0;
        if (decimals === 0 && !onlyZeros) {
            const numberStats = {
                type: 'integer',
                isOptional,
                min,
                max,
            };
            return numberStats;
        }
        const floatStats = {
            type: 'float',
            isOptional,
            decimals,
            min,
            max,
        };
        return floatStats;
    }
    // for strings check if they are dates.
    const dateFormat = isDate(uniqueValues);
    if (dateFormat) {
        const dateStats = {
            type: 'date',
            isOptional,
            luxonDateFormat: dateFormat,
        };
        return dateStats;
    }
    // just return a string type.
    let max = Number.MIN_VALUE;
    let min = Number.MAX_VALUE;
    uniqueValues.forEach((v) => {
        max = Math.max(String(v).length, max);
        min = Math.min(String(v).length, min);
    });
    // @makarand:todo - look for currencies (exclude commas, and $s)
    const stringStats = {
        type: 'string',
        isOptional,
        min,
        max,
    };
    return stringStats;
};
const generateTapeFieldStats = (csvRows, defaultValue) => {
    const keys = Object.keys(csvRows[0]);
    const analyzed = csvRows.length;
    const fieldStats = keys.map((key) => {
        const values = csvRows.map((r) => r[key]);
        const basicStats = generateBasicStats(values, defaultValue);
        (0, common_1.finleyAssert)(basicStats.strings + basicStats.numbers + basicStats.nulls ===
            csvRows.length, `mismatch in length ${basicStats.strings} + ${basicStats.numbers} + ${basicStats.nulls} !== ${csvRows.length}`);
        const typeStats = detectType(basicStats);
        if (basicStats.nulls) {
            basicStats.unique.add('null');
        }
        const uniqueValueStr = basicStats.unique.size < 8
            ? `${[...basicStats.unique.values()].join(', ')}`
            : null;
        return {
            field: key,
            ...basicStats,
            uniqueValueStr,
            typeStats,
        };
    });
    return {
        analyzed,
        fieldStats,
    };
};
exports.generateTapeFieldStats = generateTapeFieldStats;
const generateTypeDefinition = (fieldStats) => {
    // generate a comment with unique values, or generic metadata.
    const { uniqueValueStr, numbers, strings, nulls, typeStats } = fieldStats;
    let comment = uniqueValueStr || `(numbers=${numbers} strings=${strings} nulls=${nulls})`;
    if (!uniqueValueStr) {
        if (typeStats.type === 'string') {
            comment += `, min len = ${typeStats.min}, max len = ${typeStats.max}`;
        }
        else if (typeStats.type === 'integer') {
            comment += `, min = ${typeStats.min}, max = ${typeStats.max}`;
        }
        else if (typeStats.type === 'float') {
            comment += `, min = ${typeStats.min}, max = ${typeStats.max} decimals=${typeStats.decimals}`;
        }
        else if (typeStats.type === 'date') {
            comment += ` luxon format: ${typeStats.luxonDateFormat}`;
        }
        else {
            (0, common_1.finleyAssert)(false, `Unhandled type: ${typeStats}`);
        }
    }
    const type = fieldStats.typeStats.type;
    const typeSpecificFields = {};
    if (type === 'date') {
        typeSpecificFields.externalDateFormat =
            fieldStats.typeStats.luxonDateFormat;
    }
    const finalType = fieldStats.nulls ? `optional${capitalize(type)}` : type;
    return {
        field: fieldStats.field,
        type: finalType,
        description: comment,
        ...typeSpecificFields,
    };
};
exports.generateTypeDefinition = generateTypeDefinition;
const generateTapeDefinitionFromCSV = async ({ buffer, }) => {
    const csvString = common_1.finleyBuffer.toString(buffer);
    const { data, delimiter } = common_1.csvConversion.csvStringToJsonArray({
        csvString,
    });
    const { fieldStats } = (0, exports.generateTapeFieldStats)(data, '');
    const baseFields = fieldStats.map(exports.generateTypeDefinition);
    return {
        baseFields,
        delimiter,
        sanitizedFieldNames: true,
    };
};
exports.generateTapeDefinitionFromCSV = generateTapeDefinitionFromCSV;
const EXTRACT_FINLEY_PREFIX = 'FINLEY_';
/**
 * This utility facilitates the process of configuring Xlsx2Csv extractValues
 * by allowing users to upload a xlsx template with the desired cells overridden
 * by custom field name and generating the config based off this.
 * This function identifies the desired cells by ones that start with "FINLEY_"
 */
const generateXlsx2CsvExtractConfigFromXLSX = (buffer) => {
    const extract = [];
    const workbook = edit_1.default.read(buffer, { type: 'buffer', dense: true });
    for (const sheetName of workbook.SheetNames) {
        const sheet = workbook.Sheets[sheetName];
        if (!sheet) {
            return { isValid: false, errors: [`[Sheet:${sheetName}] not found`] };
        }
        const sheetRange = sheet['!ref'];
        // Skip empty sheet range, as this likely denotes it's just an empty sheet
        if (!sheetRange) {
            continue;
        }
        const { s: start, e: end } = edit_1.default.utils.decode_range(sheetRange);
        // eslint-disable-next-line no-restricted-syntax
        for (let row = start.r; row <= end.r; ++row) {
            // eslint-disable-next-line no-restricted-syntax
            for (let col = start.c; col <= end.c; ++col) {
                const cell = sheet?.[row]?.[col];
                if (!cell) {
                    continue;
                }
                const { v: value, f: formula } = cell;
                // Only care about populated cells that are strings and start with "FINLEY_" identifier.
                // Note that we also want to ignore cells that are formulas since we need to skip cells
                // that reference another cell and ends up having "FINLEY_" in it unintended
                if (formula ||
                    !value ||
                    typeof value !== 'string' ||
                    !value.startsWith(EXTRACT_FINLEY_PREFIX)) {
                    continue;
                }
                const cellAddress = common_1.utils.numberCoordsToExcelCoords({
                    r: row,
                    c: col,
                });
                // "FINLEY_commitmentAmount" would be an example configured value on the sheet
                // so splitting based on "_" delimiter and taking [1] index to get "fieldName"
                //
                // NOTE: we can also consider configuring sanitize type in this manner as well
                // if the input field is structured like "FINLEY_{fieldName}_{type}"
                const fieldName = value.split('_')[1];
                if (!fieldName) {
                    return {
                        isValid: false,
                        errors: [
                            `Found empty name [sheet:${sheetName}] [cell:${cellAddress}]`,
                        ],
                    };
                }
                extract.push({ sheetName, cellAddress, fieldName, importRaw: true });
            }
        }
    }
    return {
        isValid: true,
        configuration: { operation: 'xlsx2csv', mode: 'extractValues', extract },
    };
};
exports.generateXlsx2CsvExtractConfigFromXLSX = generateXlsx2CsvExtractConfigFromXLSX;
//# sourceMappingURL=generateTapeDefinition.js.map